import { useEffect, useMemo, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import { toast } from "sonner";

const NAVY = "#1B2A4A";
const GOLD = "#C9A94E";
const CHARCOAL = "#2D3748";

function Stars({ value, size = 16, editable = false, onChange, testidPrefix = "star" }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        const Component = editable ? "button" : "span";
        return (
          <Component
            key={n}
            type={editable ? "button" : undefined}
            onClick={editable ? () => onChange?.(n) : undefined}
            className={editable ? "p-0.5 leading-none transition-transform hover:scale-110" : "leading-none"}
            aria-label={editable ? `${n} star${n > 1 ? "s" : ""}` : undefined}
            data-testid={`${testidPrefix}-${n}`}
            style={editable ? { background: "transparent", border: "none", cursor: "pointer" } : undefined}
          >
            <Star
              width={size}
              height={size}
              strokeWidth={1.5}
              stroke={filled ? GOLD : "#B7B0A0"}
              fill={filled ? GOLD : "transparent"}
            />
          </Component>
        );
      })}
    </div>
  );
}

export default function ReviewSection({ slug }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get(`/reviews/${slug}`)
      .then(({ data }) => setReviews(Array.isArray(data) ? data : []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-line */ }, [slug]);

  const avg = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  }, [reviews]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please add your name."); return; }
    if (rating < 1) { toast.error("Please pick a star rating."); return; }
    if (text.trim().length < 3) { toast.error("Please write a short review."); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        article_slug: slug,
        reviewer_name: name.trim(),
        rating,
        review_text: text.trim(),
      });
      toast.success("Thanks — your review has been posted.");
      setName(""); setRating(0); setText("");
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save review. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="mt-12 sm:mt-16 pt-10 sm:pt-12"
      style={{ borderTop: `1px solid ${GOLD}66`, maxWidth: "720px", margin: "3rem auto 0", padding: "2.5rem 1.5rem 0" }}
      data-testid="reader-reviews"
    >
      <div className="text-center mb-6 sm:mb-8">
        <div
          className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold mb-3"
          style={{ color: GOLD }}
        >
          Reader Reviews
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl font-semibold" style={{ color: NAVY }}>
          What readers are saying
        </h3>
        {reviews.length > 0 && (
          <div className="mt-4 inline-flex items-center gap-3" data-testid="reviews-average">
            <Stars value={avg} size={18} testidPrefix="avg-star" />
            <span className="text-sm font-medium" style={{ color: NAVY }}>
              {avg.toFixed(1)}
            </span>
            <span className="text-sm" style={{ color: "#6B7280" }}>
              · {reviews.length} review{reviews.length === 1 ? "" : "s"}
            </span>
          </div>
        )}
      </div>

      {/* Existing reviews */}
      {loading ? (
        <div className="flex justify-center py-8 text-sm" style={{ color: "#5C5344" }}>
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading reviews…
        </div>
      ) : reviews.length === 0 ? (
        <p
          className="text-center italic mb-8"
          style={{ color: "#6B7280", fontSize: "0.95rem" }}
          data-testid="reviews-empty"
        >
          Be the first to share your thoughts on this book.
        </p>
      ) : (
        <ul className="space-y-5 sm:space-y-6 mb-10" data-testid="reviews-list">
          {reviews.map((r) => (
            <li
              key={r.id}
              className="rounded-2xl px-5 sm:px-6 py-5 sm:py-6"
              style={{
                background: "#ffffff",
                border: `1px solid ${GOLD}33`,
                boxShadow: "0 6px 20px -14px rgba(27, 42, 74, 0.25)",
              }}
              data-testid={`review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-medium text-sm sm:text-base" style={{ color: NAVY }}>
                    {r.reviewer_name}
                  </div>
                  <div className="mt-1"><Stars value={r.rating} size={14} testidPrefix={`review-${r.id}-star`} /></div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.14em] whitespace-nowrap pt-1" style={{ color: "#5C5344" }}>
                  {formatDate(r.created_at)}
                </div>
              </div>
              <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap" style={{ color: CHARCOAL }}>
                {r.review_text}
              </p>
            </li>
          ))}
        </ul>
      )}

      {/* Submit form */}
      <form
        onSubmit={submit}
        className="rounded-2xl px-5 sm:px-8 py-6 sm:py-8"
        style={{
          background: "linear-gradient(180deg, rgba(27,42,74,0.03) 0%, rgba(201,169,78,0.05) 100%)",
          border: `1px solid ${NAVY}22`,
        }}
        data-testid="review-form"
      >
        <h4 className="font-serif text-lg sm:text-xl font-semibold mb-4" style={{ color: NAVY }}>
          Share your take
        </h4>

        <label className="block text-xs font-semibold uppercase tracking-[0.14em] mb-1.5" style={{ color: NAVY }}>
          Your name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="How should we sign your review?"
          className="w-full px-4 py-2.5 rounded-lg text-sm sm:text-base outline-none focus:ring-2 focus:ring-offset-1"
          style={{ background: "#fff", border: `1px solid ${NAVY}22`, color: CHARCOAL }}
          data-testid="review-name-input"
          required
        />

        <div className="mt-4">
          <label className="block text-xs font-semibold uppercase tracking-[0.14em] mb-1.5" style={{ color: NAVY }}>
            Rating
          </label>
          <div data-testid="review-rating">
            <Stars value={rating} size={22} editable onChange={setRating} testidPrefix="rating-star" />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-semibold uppercase tracking-[0.14em] mb-1.5" style={{ color: NAVY }}>
            Review
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="What resonated? What surprised you? Would you recommend this book?"
            className="w-full px-4 py-3 rounded-lg text-sm sm:text-base outline-none focus:ring-2 focus:ring-offset-1 resize-y"
            style={{ background: "#fff", border: `1px solid ${NAVY}22`, color: CHARCOAL, minHeight: 110 }}
            data-testid="review-text-input"
            required
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm sm:text-base font-medium transition-all disabled:opacity-60"
          style={{ background: NAVY, color: "#fff", border: `1px solid ${NAVY}` }}
          data-testid="review-submit"
          onMouseEnter={(e) => {
            if (submitting) return;
            e.currentTarget.style.background = "#14224a";
            e.currentTarget.style.borderColor = GOLD;
            e.currentTarget.style.color = GOLD;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = NAVY;
            e.currentTarget.style.borderColor = NAVY;
            e.currentTarget.style.color = "#fff";
          }}
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</> : "Post review"}
        </button>
      </form>
    </section>
  );
}
